import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Layout from '../../components/Layout';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from 'recharts';
import DatePicker from '../../components/DatePicker';
import { useToast } from '../../components/Toast';
import {
  fetchCoupons,
  markCouponUsedFromSocket,
  removeCouponsFromSocket,
  upsertCouponsFromSocket,
} from '../../store/slices/couponSlice';
import { fetchCustomers } from '../../store/slices/customerSlice';
import { useIssuedCouponSocket } from '../../hooks/useSocketListeners';
import './Dashboard.css';

const formatLabelDate = (ymd) => {
  if (!ymd) return '';
  const parts = String(ymd).split('-');
  if (parts.length !== 3) return ymd;
  const [y, m, d] = parts;
  return `${y}/${m}/${d}`;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { coupons } = useSelector((state) => state.coupon);
  const { customers } = useSelector((state) => state.customer);
  const toast = useToast();

  const [storeFilter, setStoreFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const hasFetchedInitialData = useRef(false);

  const handleStartDateChange = (value) => {
    if (endDate && new Date(value) > new Date(endDate)) {
      toast.error('開始日は終了日より後に設定できません。');
      return;
    }
    setStartDate(value);
  };

  const handleEndDateChange = (value) => {
    if (startDate && new Date(value) < new Date(startDate)) {
      toast.error('終了日は開始日より前に設定できません。');
      return;
    }
    setEndDate(value);
  };

  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    dispatch(fetchCoupons({}));
    dispatch(fetchCustomers());
  }, [dispatch]);

  const handleCouponSocketEvent = useCallback(
    (e) => {
      const action = e?.action;
      if (action === 'created') {
        const couponsPayload = Array.isArray(e?.data?.coupons) ? e.data.coupons : [];
        if (couponsPayload.length) dispatch(upsertCouponsFromSocket(couponsPayload));
        return;
      }
      if (action === 'used') {
        dispatch(markCouponUsedFromSocket(e?.data));
        return;
      }
      if (action === 'deleted') {
        dispatch(removeCouponsFromSocket(e?.data));
      }
    },
    [dispatch]
  );

  useIssuedCouponSocket(handleCouponSocketEvent);

  const customerById = useMemo(() => {
    const map = new Map();
    (customers || []).forEach((c) => {
      if (c?.id == null) return;
      map.set(String(c.id), c);
    });
    return map;
  }, [customers]);

  const storeOptions = useMemo(() => {
    const map = new Map();
    (coupons || []).forEach((c) => {
      if (c.managerId == null) return;
      const key = String(c.managerId);
      if (!map.has(key)) {
        const customer = customerById.get(key);
        const base = customer?.storeName || '店舗';
        const label = `${base} (ID: ${key})`;
        map.set(key, { value: key, label });
      }
    });
    return [{ value: 'all', label: 'すべての店舗' }, ...Array.from(map.values())];
  }, [coupons, customerById]);

  const filteredCoupons = useMemo(() => {
    let result = coupons || [];

    if (storeFilter !== 'all') {
      result = result.filter((c) => c.managerId != null && String(c.managerId) === storeFilter);
    }

    if (startDate) {
      const s = new Date(startDate);
      result = result.filter((c) => {
        if (!c.issuedAt) return false;
        const d = new Date(c.issuedAt);
        return d >= s;
      });
    }

    if (endDate) {
      const e = new Date(endDate);
      e.setHours(23, 59, 59, 999);
      result = result.filter((c) => {
        if (!c.issuedAt) return false;
        const d = new Date(c.issuedAt);
        return d <= e;
      });
    }

    return result;
  }, [coupons, storeFilter, startDate, endDate]);

  const couponStatusSummary = useMemo(() => {
    const statusCount = filteredCoupons.reduce(
      (acc, c) => {
        const key = c.isUsed || 'その他';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {}
    );

    return [
      { label: '有効', value: statusCount['有効'] || 0 },
      { label: '保存済み', value: statusCount['保存済み'] || 0 },
      { label: '使用済み', value: statusCount['使用済み'] || 0 },
      { label: '期限切れ', value: statusCount['期限切れ'] || 0 },
    ];
  }, [filteredCoupons]);

  const couponIssuedTrend = useMemo(() => {
    const countsByDate = filteredCoupons.reduce((acc, c) => {
      if (!c.issuedAt) return acc;
      const key = c.issuedAt;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    // 決定する表示期間
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let rangeStart;
    let rangeEnd;

    if (startDate || endDate) {
      // ユーザーが期間を指定している場合
      rangeStart = startDate ? new Date(startDate) : new Date(endDate);
      rangeStart.setHours(0, 0, 0, 0);

      rangeEnd = endDate ? new Date(endDate) : today;
      rangeEnd.setHours(0, 0, 0, 0);
    } else {
      // 期間未指定の場合は直近30日
      rangeEnd = today;
      rangeStart = new Date(today);
      rangeStart.setDate(rangeStart.getDate() - 29);
    }

    const result = [];
    const cursor = new Date(rangeStart);

    while (cursor <= rangeEnd) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, '0');
      const d = String(cursor.getDate()).padStart(2, '0');
      const key = `${y}-${m}-${d}`;

      result.push({
        date: formatLabelDate(key),
        count: countsByDate[key] || 0,
      });

      cursor.setDate(cursor.getDate() + 1);
    }

    return result;
  }, [filteredCoupons, startDate, endDate]);

  const storeSummary = useMemo(() => {
    if (storeFilter !== 'all') {
      // Chọn một cửa hàng/người: chỉ một cột (người đó)
      const counts = filteredCoupons.reduce((acc, c) => {
        if (c.managerId == null) return acc;
        const id = String(c.managerId);
        const customer = customerById.get(id);
        const base = customer?.storeName || '店舗';
        const label = `${base} (ID: ${id})`;
        if (!acc[id]) {
          acc[id] = { store: label, value: 0 };
        }
        acc[id].value += 1;
        return acc;
      }, {});
      return Object.values(counts);
    }

    // Chọn "tất cả": nhóm theo cửa hàng (cùng storeName = một cột), gom danh sách ID
    const byStore = filteredCoupons.reduce((acc, c) => {
      if (c.managerId == null) return acc;
      const id = String(c.managerId);
      const customer = customerById.get(id);
      const storeName = (customer?.storeName || '').trim();
      const key = storeName || `__id_${id}`;
      if (!acc[key]) {
        acc[key] = { storeName, ids: [], value: 0 };
      }
      if (!acc[key].ids.includes(id)) {
        acc[key].ids.push(id);
      }
      acc[key].value += 1;
      return acc;
    }, {});

    return Object.values(byStore).map(({ storeName, ids, value }) => ({
      store: storeName ? `${storeName} (ID: ${ids.join(', ')})` : `店舗 (ID: ${ids.join(', ')})`,
      value,
    }));
  }, [filteredCoupons, customerById, storeFilter]);

  const totalCoupons = filteredCoupons.length;
  const activeCoupons = couponStatusSummary.find((i) => i.label === '有効')?.value ?? 0;
  const savedCoupons = couponStatusSummary.find((i) => i.label === '保存済み')?.value ?? 0;
  const usedCoupons = couponStatusSummary.find((i) => i.label === '使用済み')?.value ?? 0;
  const expiredUnusedCoupons =
    couponStatusSummary.find((i) => i.label === '期限切れ')?.value ?? 0;

  const selectedStoreOption =
    storeOptions.find((opt) => opt.value === storeFilter) || storeOptions[0];

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard__header-card">
          <div className="dashboard__title-row">
            <h1 className="dashboard__title">ダッシュボード</h1>
            <p className="dashboard__subtitle">
            条件指定によるクーポン発行統計。
            </p>
          </div>

          <div className="dashboard__filters">
            <div className="dashboard__filter-group">
              <label className="dashboard__filter-label">店舗</label>
              <Select
                className="coupon__filter-select-container"
                classNamePrefix="coupon__filter-select-container"
                options={storeOptions}
                value={selectedStoreOption}
                onChange={(opt) => setStoreFilter(opt?.value ?? 'all')}
                maxMenuHeight={180}
                isSearchable={false}
              />
            </div>

            <div className="dashboard__filter-group dashboard__filter-group--date">
              <label className="dashboard__filter-label">期間</label>
              <div className="dashboard__filter-date-range">
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  className="dashboard__date-picker"
                />
                <span className="dashboard__filter-date-separator">〜</span>
                <DatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  className="dashboard__date-picker"
                />
                <button
                  type="button"
                  className="dashboard__filter-clear"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                >
                  クリア
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard__grid">
          <div className="dashboard__stat-card">
            <div className="dashboard__stat-label">合計クーポン数</div>
            <div className="dashboard__stat-value">{totalCoupons.toLocaleString()}</div>
            <div className="dashboard__stat-hint">発行済みの全クーポン</div>
          </div>

          <div className="dashboard__stat-card">
            <div className="dashboard__stat-label">有効クーポン</div>
            <div className="dashboard__stat-value dashboard__stat-value--success">
              {activeCoupons.toLocaleString()}
            </div>
            <div className="dashboard__stat-hint">現在利用可能なクーポン</div>
          </div>

          <div className="dashboard__stat-card">
            <div className="dashboard__stat-label">保存済みクーポン</div>
            <div className="dashboard__stat-value dashboard__stat-value--warning">
              {savedCoupons.toLocaleString()}
            </div>
            <div className="dashboard__stat-hint">顧客に保存済みのクーポン</div>
          </div>

          <div className="dashboard__stat-card">
            <div className="dashboard__stat-label">使用済みクーポン</div>
            <div className="dashboard__stat-value dashboard__stat-value--info">
              {usedCoupons.toLocaleString()}
            </div>
            <div className="dashboard__stat-hint">利用完了したクーポン</div>
          </div>

          <div className="dashboard__stat-card">
            <div className="dashboard__stat-label">期限切れ未使用クーポン</div>
            <div className="dashboard__stat-value dashboard__stat-value--danger">
              {expiredUnusedCoupons.toLocaleString()}
            </div>
            <div className="dashboard__stat-hint">有効期限切れで未使用のクーポン</div>
          </div>
        </div>

        <div className="dashboard__charts">
          <div className="dashboard__chart-card">
            <div className="dashboard__chart-header">
              <h2 className="dashboard__chart-title">直近7日間のクーポン発行数</h2>
              <span className="dashboard__chart-caption">
                日別の発行推移（期間指定がない場合は直近30日）
              </span>
            </div>
            <div className="dashboard__chart-body">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={couponIssuedTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E7FF" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value) => [`${value} 件`, '発行数']}
                    labelFormatter={(label) => `日付: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#5cb3e8"
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dashboard__chart-card">
            <div className="dashboard__chart-header">
              <h2 className="dashboard__chart-title">店舗別クーポン発行数</h2>
              <span className="dashboard__chart-caption">現在のフィルタ条件に基づく店舗別の発行数</span>
            </div>
            <div className="dashboard__chart-body dashboard__chart-body--bar">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={storeSummary}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  barCategoryGap={30}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E7FF" />
                  <XAxis dataKey="store" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${value} 件`, 'クーポン数']} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#82C4F4" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
